import React from 'react';
import classNames from 'classnames';
import s from './markdown.module.scss';

/**
 * Лёгкий рендерер markdown для текста сообщений.
 *
 * Поддерживает базовый синтаксис:
 *  - **жирный** / __жирный__
 *  - *курсив* / _курсив_
 *  - ~~зачёркнутый~~
 *  - `встроенный код`
 *  - ``` блок кода ```
 *  - [ссылка](https://...)
 *  - списки (-, *, + и 1.), цитаты (>)
 *
 * Особенный синтаксис для выделения текста:
 *  - ==выделенный текст== — рендерится как <mark> («маркер»)
 *
 * Рендер происходит только через React-элементы (без dangerouslySetInnerHTML),
 * поэтому ввод пользователя безопасен.
 */

// Порядок альтернатив важен: двойные символы проверяются раньше одиночных
const INLINE_RE =
  /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|==[^=]+==|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s](?:[^*]*[^*\s])?\*|_[^_\s](?:[^_]*[^_\s])?_)/g;

const renderInline = (text, keyPrefix = 'i') =>
  String(text)
    .split(INLINE_RE)
    .filter(Boolean)
    .map((part, index) => {
      const key = `${keyPrefix}-${index}`;

      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('~~') && part.endsWith('~~')) {
        return <del key={key}>{part.slice(2, -2)}</del>;
      }
      if (part.startsWith('==') && part.endsWith('==')) {
        return (
          <mark key={key} className={s.mark}>
            {part.slice(2, -2)}
          </mark>
        );
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <code key={key} className={s.code}>
            {part.slice(1, -1)}
          </code>
        );
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }
      if (part.startsWith('_') && part.endsWith('_')) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }

      const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (link) {
        return (
          <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer">
            {link[1]}
          </a>
        );
      }

      return <React.Fragment key={key}>{part}</React.Fragment>;
    });

const UL_RE = /^[-*+]\s+(.*)$/;
const OL_RE = /^\d+\.\s+(.*)$/;
const QUOTE_RE = /^>\s?(.*)$/;
const FENCE_RE = /^```/;

const renderBlocks = (text) => {
  const lines = String(text).split('\n');
  const blocks = [];
  let paragraph = [];
  let list = null; // { ordered: boolean, items: string[] }

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(
      <p key={`p-${blocks.length}`}>
        {paragraph.map((line, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <br />}
            {renderInline(line, `p-${blocks.length}-${idx}`)}
          </React.Fragment>
        ))}
      </p>
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const items = list.items.map((item, idx) => (
      <li key={idx}>{renderInline(item, `li-${blocks.length}-${idx}`)}</li>
    ));
    blocks.push(
      list.ordered
        ? <ol key={`l-${blocks.length}`}>{items}</ol>
        : <ul key={`l-${blocks.length}`}>{items}</ul>
    );
    list = null;
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Блок кода: ```
    if (FENCE_RE.test(trimmed)) {
      flushParagraph();
      flushList();
      const buf = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i].trim())) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // пропускаем закрывающий ``` (или конец текста)
      blocks.push(
        <pre key={`cb-${blocks.length}`} className={s.codeBlock}>
          <code>{buf.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Списки
    const ulMatch = trimmed.match(UL_RE);
    const olMatch = trimmed.match(OL_RE);
    if (ulMatch || olMatch) {
      flushParagraph();
      const ordered = !ulMatch;
      if (!list || list.ordered !== ordered) flushList();
      list = list && list.ordered === ordered ? list : { ordered, items: [] };
      list.items.push((ulMatch || olMatch)[1]);
      i += 1;
      continue;
    }
    flushList();

    // Цитаты
    const quoteMatch = trimmed.match(QUOTE_RE);
    if (quoteMatch) {
      flushParagraph();
      const buf = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(QUOTE_RE);
        if (!m) break;
        buf.push(m[1]);
        i += 1;
      }
      blocks.push(
        <blockquote key={`q-${blocks.length}`}>
          {buf.map((l, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {renderInline(l, `q-${blocks.length}-${idx}`)}
            </React.Fragment>
          ))}
        </blockquote>
      );
      continue;
    }

    // Пустая строка разрывает абзац
    if (!trimmed) {
      flushParagraph();
      i += 1;
      continue;
    }

    paragraph.push(line);
    i += 1;
  }

  flushParagraph();
  flushList();
  return blocks;
};

export const MarkdownText = ({ text, className }) => (
  <div className={classNames(s.markdown, className)}>{renderBlocks(text)}</div>
);
